/**
 * Reactive mirror of the tour's session-scoped dismissal key.
 *
 * `useIsTourBlocking` — and therefore the PII / account-setup / expiry
 * sequence, the contact-details popup and the child-aged-out popup — is gated
 * on the tour's `isEligible`, whose last condition is "has the user dismissed
 * the tour in this browser session". That answer lives in `sessionStorage`,
 * which is not reactive: writing the key changes nothing on screen. The exit
 * path used to paper over that with `window.location.reload()`, which was the
 * only reason the other popups ever noticed the dismissal.
 *
 * With the reload gone, the write has to notify React. `useSyncExternalStore`
 * over a module-cached snapshot does exactly that, mirroring the pattern
 * already used by `menuDrawerControl.ts` in this folder.
 *
 * `sessionStorage` stays the durable record rather than Redux: it survives F5
 * (so a dismissal is not undone by a refresh), it is cleared on sign-out in
 * `app/store.ts`, and it keeps this out of the persist whitelist, where a
 * transient suppression flag does not belong.
 */
import { useSyncExternalStore } from 'react';
import { ONBOARDING_MODAL_VIEWED_KEY } from './constants';

/* Cached so `getSnapshot` returns a stable primitive rather than re-reading
   storage on every render — React calls it on each render and after every
   notification. Seeded from storage so a refresh mid-session is honoured. */
let isDismissed = sessionStorage.getItem(ONBOARDING_MODAL_VIEWED_KEY) !== null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return isDismissed;
}

/**
 * Records that the user dismissed the tour — Skip, "Don't show again" or
 * Finish — and releases every popup that was waiting behind it.
 *
 * Raw `sessionStorage` deliberately, NOT the `setInStorage` helper: that
 * JSON-encodes, so `'true'` would be stored as `"true"` and would not match
 * Angular's plain string or the sign-out cleanup in `app/store.ts`.
 */
export function markTourDismissed() {
  sessionStorage.setItem(ONBOARDING_MODAL_VIEWED_KEY, 'true');
  if (isDismissed) return;
  isDismissed = true;
  emit();
}

/**
 * Re-arms the tour for the next user in this page load.
 *
 * Called from the `SIGNOUT_REQUEST` handler in `app/store.ts`, which is where
 * the key used to be removed directly. It has to go through this store now:
 * clearing storage alone would leave the module cache reading `true`, so a
 * sign-out followed by a sign-in without a page load would suppress the tour
 * for the next user.
 */
export function clearTourDismissed() {
  sessionStorage.removeItem(ONBOARDING_MODAL_VIEWED_KEY);
  if (!isDismissed) return;
  isDismissed = false;
  emit();
}

/** Subscribe to "the tour has been dismissed in this session". */
export function useIsTourDismissed() {
  return useSyncExternalStore(subscribe, getSnapshot);
}
